import Link from "next/link";
import { useRouter } from "next/router";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";

import styles from "./style.module.scss";

type CollectionPageTabsProps = {
  slug: string;
};

const CollectionPageTabs = ({ slug }: CollectionPageTabsProps) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { trackEvent } = useActivityTracker();
  const { account: loggedInAccount } = useAuthentication();

  const tabs = useMemo(
    () => [
      {
        name: "Research",
        href: `/collections/${slug}/research`,
      },

      {
        name: "Discussions",
        noFollow: true,
        href: `/collections/${slug}/discussions`,
      },

      ...(loggedInAccount &&
      !loggedInAccount.isPreviewPass &&
      loggedInAccount.canViewInsightThemes
        ? [
            {
              name: "Themes",
              customName: (
                <span className="flex">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://assets.smartkarma.com/icons/sparkle.svg"
                    alt="Quiddity Northbound Monitor"
                    width={mobile ? 15 : 20}
                    height={mobile ? 14 : 22}
                    className="pb-[2px] md:absolute left-[-2px]"
                  />
                  Themes
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        Themes are AI-extracted nuggets of information,
                        organized into topics of interest based on premium
                        insights from Smartkarma. Each theme represents a
                        concise, valuable piece of information from
                        comprehensive analysis, giving users a dynamic
                        understanding of evolving market trends.
                      </Tooltip>
                    }
                  >
                    <span className="hidden md:inline">
                      <FontAwesomeIcon
                        className="ml-[2px] md:ml-[10px] pb-[4px] md:pb-[1px] text-[15px] text-gray-gray"
                        icon={faExclamationCircle}
                      />
                    </span>
                  </OverlayTrigger>
                </span>
              ),
              href: `/collections/${slug}/themes`,
              className: "md:!pl-[40px] ml-[-20px]",
            },
          ]
        : []),

      ...(mobile
        ? [
            {
              name: "More",
              noFollow: true,
              href: `/collections/${slug}/more`,
            },
          ]
        : []),
    ],
    [loggedInAccount, mobile, slug]
  );

  const scrollableRef = useRef<HTMLDivElement>(null);

  const onTabClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, tab: string) => {
      try {
        trackEvent("Tab - Click", {
          event_category: "Tab",
          event_action: "Click",
          event_label: tab,
        });
      } catch (error) {
        console.debug(error);
      }
    },
    [trackEvent]
  );

  const activeTab = useRef<HTMLSpanElement | null>(null);
  const onTabLoad = useCallback(
    (ref, tab) => {
      if (
        !!ref &&
        (router.asPath === tab.href || router.asPath.startsWith(`${tab.href}?`))
      ) {
        activeTab.current = ref;
      }
    },
    [router.asPath]
  );

  const [stickyTop, setStickyTop] = useState(false);
  const [top, setTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!mobile) return;

    const container = containerRef.current;
    const upperNavBar = 0;

    const updateTabsPosition = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        const top =
          container?.getBoundingClientRect()?.top || Number.MAX_SAFE_INTEGER;
        setStickyTop(top < upperNavBar);
        setTop(top < 0 ? 0 : top);
      }, 1);
    };

    updateTabsPosition();

    window.addEventListener("scroll", updateTabsPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateTabsPosition);
    };
  }, [mobile]);

  return (
    <>
      <div
        className={classNames(
          styles.placeholder,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs
        )}
        ref={containerRef}
      />
      <div
        className={classNames(
          styles.entityPageTabs,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs,
          "mx-[-5px] rounded-none bg-white shadow-md mb-[-15px] z-10",
          "md:mx-0 md:rounded-xl md:bg-transparent md:shadow-none md:mb-0",
          "skiptranslate"
        )}
        style={stickyTop ? { top } : {}}
      >
        <div className={classNames(styles.tabs)} ref={scrollableRef}>
          {tabs.map((tab: any) => {
            return (
              <Link
                href={tab.href}
                key={tab.name}
                rel={tab.noFollow && "nofollow"}
                prefetch={false}
                className={twMerge(
                  router.asPath === tab.href ||
                    router.asPath.startsWith(`${tab.href}?`)
                    ? styles.activeTab
                    : undefined,
                  tab.className
                )}
                ref={(ref) => onTabLoad(ref, tab)}
                onClick={(e) => onTabClick(e, tab.name)}
              >
                {tab.customName || tab.name}
              </Link>
            );
          })}
        </div>
      </div>
      {stickyTop && <div className="h-[48px] w-full" />}
    </>
  );
};

export default CollectionPageTabs;
